// Backend-agnostic auth adapter. All authentication logic in the app should
// import from this module rather than talking to the backend directly. The
// current implementation is backed by Lovable Cloud; swap the internals
// without touching call sites.
import { supabase } from "@/integrations/supabase/client";
import { track, identify, rememberIdentity } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";

export type AuthResult = { error: string | null; code?: "email_not_confirmed" };

/** Where verification links land; the page reports success or an expired link. */
function verifyRedirect(): string | undefined {
  return typeof window !== "undefined" ? `${window.location.origin}/verify-email` : undefined;
}

/**
 * Customer-friendly wording for verification email problems. Never reveals
 * whether an address is registered.
 */
export function verificationErrorMessage(error: unknown): string {
  const raw =
    error && typeof error === "object" && "message" in error
      ? String((error as { message: unknown }).message)
      : "";
  const m = raw.toLowerCase();
  const wait = raw.match(/after (\d+) seconds?/i);
  if (wait) return `Please wait ${wait[1]} seconds before requesting another email.`;
  if (m.includes("rate limit") || m.includes("too many")) {
    return "Too many emails requested. Please wait a few minutes and try again.";
  }
  if (m.includes("sending") || m.includes("smtp") || m.includes("deliver")) {
    return "We couldn't send the verification email right now. Please try again in a few minutes.";
  }
  if (m.includes("invalid") && m.includes("email")) return "Enter a valid email address.";
  return "We couldn't send the verification email. Please try again.";
}

/**
 * Message shown to a customer whose account has been blocked by an admin.
 * The internal block reason is never exposed to the customer.
 */
export const BLOCKED_ACCOUNT_MESSAGE =
  "Your account has been temporarily suspended. Please contact customer support.";

function isBlockedError(message: string) {
  const m = message.toLowerCase();
  return m.includes("banned") || m.includes("user_banned");
}

function toMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message: unknown }).message);
    return isBlockedError(message) ? BLOCKED_ACCOUNT_MESSAGE : message;
  }
  return "Something went wrong. Please try again.";
}

export async function signUp(email: string, password: string): Promise<AuthResult> {
  const redirectTo = typeof window !== "undefined" ? window.location.origin : undefined;
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: verifyRedirect() ?? redirectTo },
  });
  if (error && /sending|smtp/i.test(error.message)) {
    return { error: verificationErrorMessage(error) };
  }
  if (!error) {
    identify(email, null, { registration_date: new Date().toISOString(), customer_status: "lead" });
    track(CIO_EVENTS.USER_REGISTERED, { email });
  }
  return { error: error ? toMessage(error) : null };
}

export async function signIn(email: string, password: string): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error && /email not confirmed/i.test(error.message)) {
    return { error: "Verify your email before signing in.", code: "email_not_confirmed" };
  }
  if (!error) {
    const { data } = await supabase.auth.getUser();
    identify(email, data.user?.id ?? null, { last_login: new Date().toISOString() });
    track(CIO_EVENTS.USER_LOGIN, { email, customer_id: data.user?.id ?? null });
    if (data.user?.email_confirmed_at) track(CIO_EVENTS.EMAIL_VERIFIED, { email });
  }
  return { error: error ? toMessage(error) : null };
}

/**
 * Re-send the sign-up verification email. Uses the existing account (no new
 * account is created) and responds the same way for any address.
 */
export async function resendVerificationEmail(email: string): Promise<AuthResult> {
  const { error } = await supabase.auth.resend({
    type: "signup",
    email,
    options: { emailRedirectTo: verifyRedirect() },
  });
  return { error: error ? verificationErrorMessage(error) : null };
}

export async function signOut(): Promise<AuthResult> {
  track(CIO_EVENTS.USER_LOGOUT, {});
  const { error } = await supabase.auth.signOut();
  if (!error) rememberIdentity(null, null);
  return { error: error ? toMessage(error) : null };
}

export async function requestPasswordReset(email: string): Promise<AuthResult> {
  const redirectTo =
    typeof window !== "undefined" ? `${window.location.origin}/reset-password` : undefined;
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo,
  });
  if (!error) track(CIO_EVENTS.PASSWORD_RESET_REQUESTED, { email });
  return { error: error ? toMessage(error) : null };
}

export async function sendMagicLink(email: string): Promise<AuthResult> {
  const redirectTo =
    typeof window !== "undefined" ? `${window.location.origin}/account` : undefined;
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo },
  });
  if (!error) identify(email, null, {});
  return { error: error ? toMessage(error) : null };
}

export async function updatePassword(password: string): Promise<AuthResult> {
  const { error } = await supabase.auth.updateUser({ password });
  if (!error) track(CIO_EVENTS.PASSWORD_RESET_COMPLETED, {});
  return { error: error ? toMessage(error) : null };
}

export async function getCurrentUser() {
  const { data } = await supabase.auth.getUser();
  return data.user;
}

export function onAuthChange(cb: (signedIn: boolean) => void) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    cb(!!session);
  });
  return () => data.subscription.unsubscribe();
}
